import { useMutation, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  checkUserTerms,
  queries,
  service,
  useAuthConfig,
} from "@theprelude/core";
import { Button, Checkbox, UploadIcon, notifyError } from "@theprelude/ds";
import React, { useState } from "react";
import { setPageTitle } from "@/components/app-shell/app-shell";
import { AcceptTermsModal } from "@/components/modal/accept-terms-modal";
import { authorizeFeatures } from "@/lib/authorize-feature";

function generateRandomNumber(min: number, max: number) {
  return Math.round(Math.random() * (max - min) + min);
}

export const Route = createFileRoute("/_app/threat-intel/import/upload")({
  beforeLoad: async ({ context }) => {
    setPageTitle("Create a Threat");
    await authorizeFeatures(context, ["detect", "threat_intel"]);
  },
  loaderDeps: () => {
    const randomBackgroundNumber = generateRandomNumber(1, 6);
    const randomElementNumber = generateRandomNumber(1, 7);
    return { randomBackgroundNumber, randomElementNumber };
  },
  component: ThreatIntelPage,
});

function ThreatIntelPage() {
  return (
    <main className="flex flex-col items-center justify-center gap-8 p-8 lg:flex-row">
      <FileHandler />
    </main>
  );
}

const FileHandler = () => {
  const [forceAI, setForceAI] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const authConfig = useAuthConfig();
  const navigate = useNavigate();

  const account = useSuspenseQuery(queries.iam.getAccount(authConfig)).data;

  const areTermsAccepted = checkUserTerms({
    account,
    terms: "threat_intel",
    version: "1",
  });

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    handleFile(selectedFile);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFile = event.dataTransfer.files?.[0];
    handleFile(droppedFile);
  };

  const handleFile = (file?: File) => {
    if (!file) {
      return;
    }
    if (file.type !== "application/pdf") {
      return notifyError({
        message: "File type must be a .pdf",
        dismissible: true,
      });
    }
    if (file.size > 5_000_000) {
      return notifyError({
        message: "File size must be under 5MB",
        dismissible: true,
      });
    }
    setFile(file);
  };

  const uploadFile = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error("Please select a file to upload.");
      }

      return await service(authConfig).generate.uploadThreatIntel(
        file,
        forceAI,
      );
    },
    onSuccess: async ({ job_id }) => {
      await navigate({ to: "/threat-intel/$id", params: { id: job_id } });
    },
    onError: async (error) => {
      notifyError({
        message: "Error occurred while trying to upload the file",
        error,
        dismissible: true,
      });
    },
  });

  return (
    <>
      <AcceptTermsModal areTermsAccepted={areTermsAccepted} />

      <div
        onDrop={handleDrop}
        onDragOver={(event) => event.preventDefault()}
        className="flex w-full flex-col items-center justify-center gap-8 rounded-lg border border-stroke-line bg-white px-10 py-8 text-base dark:bg-surface-background lg:h-[584px]"
      >
        <span>Please upload a piece of threat intelligence to begin.</span>
        {file ? (
          <>
            <div className="flex flex-col items-center gap-8 text-base">
              <div className="flex flex-col items-center gap-2">
                <img src="/images/pdf-icon.png" className="h-[58px] w-[46px]" />
                <span>{file.name}</span>
              </div>
              <div className="border border-status-yellow bg-[#FFBF441A] p-4">
                By clicking &quot;Continue,&quot; you represent that you have
                the full legal right, authority, and permission to use and
                upload the threat intelligence document to the Detect platform.
                You further warrant that the submission of this document does
                not infringe upon any third-party rights, or violate any
                applicable laws, regulations or policies, including those
                related to data protection, privacy or confidentiality, and you
                hereby agree to indemnify Prelude against any claims, damages,
                losses, liabilities, or expenses arising from the
                document&apos;s upload or use.
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={!forceAI}
                  onCheckedChange={() => {
                    setForceAI(!forceAI);
                  }}
                />
                <span>
                  Use Prelude Verified Tests instead of AI-generated tests when
                  possible.
                </span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                intent="primary"
                onClick={() => uploadFile.mutate()}
                disabled={uploadFile.isPending}
                loading={uploadFile.isPending}
              >
                Continue
              </Button>
              <Button intent="secondary" onClick={() => setFile(null)}>
                Cancel
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center text-primary-foreground">
              <UploadIcon />
              <span className="mt-4">Drag and drop files here.</span>
              <span className="text-sm text-secondary-foreground">
                {"Limit 5MB (.PDF only)"}
              </span>
            </div>
            <input
              type="file"
              hidden
              id="browse"
              onChange={handleFileChange}
              accept=".pdf"
            />
            <label
              htmlFor="browse"
              className="cursor-pointer rounded border border-stroke-input bg-white px-3 py-2 hover:border-primary-foreground hover:bg-secondary-background dark:bg-secondary-background dark:hover:bg-transparent-black"
            >
              Browse...
            </label>
          </>
        )}
      </div>
    </>
  );
};
